import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import PlatformLayout from '../../components/layout/PlatformLayout';
import './Platform.css';
import {
  Plus, Search, Building2, Globe, MapPin, ChevronDown, ChevronUp,
  Edit2, ToggleLeft, ToggleRight, X, Link2, Unlink, Check, AlertCircle
} from 'lucide-react';

const SCOPES = ['local', 'regional', 'national', 'international'];
const TIERS = ['platinum', 'gold', 'silver', 'standard'];
const CATEGORIES = [
  'Sport Equipment', 'Food & Drink', 'Insurance', 'Finance',
  'Health & Wellbeing', 'Transport', 'Technology', 'Retail',
  'Media', 'Education', 'Hospitality', 'Other'
];

const TIER_COLORS = {
  platinum: '#6B6B8A',
  gold: '#C4943A',
  silver: '#888',
  standard: '#1E4A35'
};

const SCOPE_ICONS = {
  local: '📍', regional: '🗺️', national: '🏴', international: '🌍'
};

const emptyForm = {
  businessName: '', contactName: '', contactEmail: '', contactPhone: '',
  website: '', logoUrl: '', description: '', category: '', geographicScope: 'local', notes: ''
};

export default function PlatformSponsors() {
  const { platformAxios } = usePlatformAuth();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');

  // Club link modal
  const [linkModal, setLinkModal] = useState(null); // { sponsorId, sponsorName }
  const [linkForm, setLinkForm] = useState({ clubId: '', sponsorId: '', tier: 'standard', dealValue: '', currency: 'GBP', startDate: '', endDate: '', notes: '' });
  const [linkError, setLinkError] = useState('');

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: ['platform-sponsors'],
    queryFn: () => platformAxios.get('/sponsors').then(r => r.data)
  });

  const { data: allClubs = [] } = useQuery({
    queryKey: ['platform-clubs'],
    queryFn: () => platformAxios.get('/clubs').then(r => r.data)
  });

  const { data: clubLinks = [], isLoading: linksLoading } = useQuery({
    queryKey: ['sponsor-clubs', expandedId],
    queryFn: () => platformAxios.get(`/sponsors/${expandedId}/clubs`).then(r => r.data),
    enabled: !!expandedId
  });

  // Load existing club-level sponsors when a club is selected in the link modal
  const { data: clubLevelSponsors = [] } = useQuery({
    queryKey: ['club-level-sponsors', linkForm.clubId],
    queryFn: () => platformAxios.get(`/clubs/${linkForm.clubId}/sponsors`).then(r => r.data),
    enabled: !!linkForm.clubId
  });

  const createMutation = useMutation({
    mutationFn: (data) => platformAxios.post('/sponsors', data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform-sponsors'] }); setShowAddModal(false); setForm(emptyForm); setFormError(''); },
    onError: (err) => setFormError(err.response?.data?.error || 'Failed to create sponsor')
  });

  const editMutation = useMutation({
    mutationFn: ({ id, data }) => platformAxios.patch(`/sponsors/${id}`, data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platform-sponsors'] }); setEditingSponsor(null); setForm(emptyForm); setFormError(''); },
    onError: (err) => setFormError(err.response?.data?.error || 'Failed to save changes')
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => platformAxios.patch(`/sponsors/${id}/status`, { isActive }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-sponsors'] })
  });

  const linkMutation = useMutation({
    mutationFn: ({ sponsorId, data }) => platformAxios.post(`/sponsors/${sponsorId}/clubs`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sponsor-clubs', linkModal?.sponsorId] });
      qc.invalidateQueries({ queryKey: ['platform-sponsors'] });
      setLinkModal(null);
      setLinkForm({ clubId: '', sponsorId: '', tier: 'standard', dealValue: '', currency: 'GBP', startDate: '', endDate: '', notes: '' });
      setLinkError('');
    },
    onError: (err) => setLinkError(err.response?.data?.error || 'Failed to link club')
  });

  const unlinkMutation = useMutation({
    mutationFn: ({ sponsorId, clubId }) => platformAxios.delete(`/sponsors/${sponsorId}/clubs/${clubId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sponsor-clubs', expandedId] });
      qc.invalidateQueries({ queryKey: ['platform-sponsors'] });
    }
  });

  const filtered = useMemo(() => {
    return sponsors.filter(s => {
      if (!showInactive && !s.is_active) return false;
      if (scopeFilter && s.geographic_scope !== scopeFilter) return false;
      if (categoryFilter && s.category !== categoryFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return s.business_name.toLowerCase().includes(q) ||
          (s.contact_name || '').toLowerCase().includes(q) ||
          (s.category || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [sponsors, search, scopeFilter, categoryFilter, showInactive]);

  const openEdit = (s) => {
    setEditingSponsor(s);
    setForm({
      businessName: s.business_name || '',
      contactName: s.contact_name || '',
      contactEmail: s.contact_email || '',
      contactPhone: s.contact_phone || '',
      website: s.website || '',
      logoUrl: s.logo_url || '',
      description: s.description || '',
      category: s.category || '',
      geographicScope: s.geographic_scope || 'local',
      notes: s.notes || ''
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.businessName.trim()) { setFormError('Business name is required'); return; }
    if (editingSponsor) {
      editMutation.mutate({ id: editingSponsor.id, data: form });
    } else {
      createMutation.mutate(form);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingSponsor(null);
    setForm(emptyForm);
    setFormError('');
  };

  const handleLink = (e) => {
    e.preventDefault();
    if (!linkForm.clubId) { setLinkError('Please select a club'); return; }
    linkMutation.mutate({ sponsorId: linkModal.sponsorId, data: linkForm });
  };

  const linkedClubIds = new Set(clubLinks.map(l => l.club_id));
  const availableClubs = allClubs.filter(c => !linkedClubIds.has(c.id));

  return (
    <PlatformLayout>
      <div className="platform-page">
        <div className="page-header">
          <div>
            <h1>Sponsors</h1>
            <p>Manage brands and their club sponsorships across the platform</p>
          </div>
          <button className="btn-primary" onClick={() => { setEditingSponsor(null); setForm(emptyForm); setShowAddModal(true); }}>
            <Plus size={16} /> Add Sponsor
          </button>
        </div>

        {/* Stats strip */}
        <div className="sponsor-stats-strip">
          <div className="stat-pill">
            <span className="stat-num">{sponsors.filter(s => s.is_active).length}</span>
            <span className="stat-lbl">Active Sponsors</span>
          </div>
          <div className="stat-pill">
            <span className="stat-num">{sponsors.reduce((sum, s) => sum + parseInt(s.club_count || 0), 0)}</span>
            <span className="stat-lbl">Club Relationships</span>
          </div>
          <div className="stat-pill">
            <span className="stat-num">{sponsors.reduce((sum, s) => sum + parseInt(s.total_redemptions || 0), 0)}</span>
            <span className="stat-lbl">Total Redemptions</span>
          </div>
          <div className="stat-pill">
            <span className="stat-num">
              £{sponsors.reduce((sum, s) => sum + parseFloat(s.total_value || 0), 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
            <span className="stat-lbl">Attributed Value</span>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search sponsors..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select value={scopeFilter} onChange={e => setScopeFilter(e.target.value)} className="filter-select">
            <option value="">All scopes</option>
            {SCOPES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="filter-select">
            <option value="">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label className="toggle-label">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <span className="results-count">{filtered.length} of {sponsors.length}</span>
        </div>

        {/* Sponsors list */}
        {isLoading ? (
          <div className="loading-state"><div className="spinner"></div><p>Loading sponsors...</p></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>No sponsors yet</h3>
            <p>Add your first platform sponsor to get started</p>
            <button className="btn-primary" onClick={() => setShowAddModal(true)}>
              <Plus size={16} /> Add Sponsor
            </button>
          </div>
        ) : (
          <div className="sponsors-list">
            {filtered.map(sponsor => {
              const isExpanded = expandedId === sponsor.id;
              return (
                <div key={sponsor.id} className={`sponsor-card ${!sponsor.is_active ? 'inactive' : ''}`}>
                  <div className="sponsor-card-header" onClick={() => setExpandedId(isExpanded ? null : sponsor.id)}>
                    <div className="sponsor-logo-block">
                      {sponsor.logo_url ? (
                        <img src={sponsor.logo_url} alt={sponsor.business_name} className="sponsor-logo-img" />
                      ) : (
                        <div className="sponsor-logo-placeholder">
                          <Building2 size={22} />
                        </div>
                      )}
                    </div>
                    <div className="sponsor-main-info">
                      <div className="sponsor-name-row">
                        <h3>{sponsor.business_name}</h3>
                        {!sponsor.is_active && <span className="badge-inactive">Inactive</span>}
                        <span className="badge-scope">{SCOPE_ICONS[sponsor.geographic_scope]} {sponsor.geographic_scope}</span>
                        {sponsor.category && <span className="badge-category">{sponsor.category}</span>}
                      </div>
                      {sponsor.contact_name && <p className="sponsor-contact">{sponsor.contact_name}{sponsor.contact_email ? ` · ${sponsor.contact_email}` : ''}</p>}
                    </div>
                    <div className="sponsor-meta-stats">
                      <div className="meta-stat">
                        <span className="meta-val">{sponsor.club_count || 0}</span>
                        <span className="meta-lbl">clubs</span>
                      </div>
                      <div className="meta-stat">
                        <span className="meta-val">{sponsor.total_redemptions || 0}</span>
                        <span className="meta-lbl">redemptions</span>
                      </div>
                      <div className="meta-stat">
                        <span className="meta-val">£{parseFloat(sponsor.total_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                        <span className="meta-lbl">attributed</span>
                      </div>
                    </div>
                    <div className="sponsor-actions" onClick={e => e.stopPropagation()}>
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(sponsor)}><Edit2 size={15} /></button>
                      <button
                        className="btn-icon"
                        title={sponsor.is_active ? 'Deactivate' : 'Activate'}
                        onClick={() => statusMutation.mutate({ id: sponsor.id, isActive: !sponsor.is_active })}
                      >
                        {sponsor.is_active ? <ToggleRight size={18} style={{ color: '#1E4A35' }} /> : <ToggleLeft size={18} style={{ color: '#aaa' }} />}
                      </button>
                    </div>
                    <div className="expand-chevron">
                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="sponsor-detail-panel">
                      {sponsor.description && <p className="sponsor-description">{sponsor.description}</p>}

                      <div className="panel-section">
                        <div className="panel-section-header">
                          <h4>Club Relationships ({clubLinks.length})</h4>
                          <button className="btn-secondary btn-sm" onClick={() => { setLinkModal({ sponsorId: sponsor.id, sponsorName: sponsor.business_name }); setLinkError(''); }}>
                            <Link2 size={13} /> Link to Club
                          </button>
                        </div>

                        {linksLoading ? (
                          <p className="loading-text">Loading clubs...</p>
                        ) : clubLinks.length === 0 ? (
                          <p className="empty-text">Not yet linked to any clubs.</p>
                        ) : (
                          <table className="links-table">
                            <thead>
                              <tr>
                                <th>Club</th>
                                <th>Sport</th>
                                <th>Tier</th>
                                <th>Deal Value</th>
                                <th>Period</th>
                                <th>Redemptions</th>
                                <th>Value</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {clubLinks.map(link => (
                                <tr key={link.link_id}>
                                  <td><strong>{link.club_name}</strong><br /><small>{link.location}</small></td>
                                  <td>{link.sport || '—'}</td>
                                  <td>
                                    <span className="tier-badge" style={{ borderColor: TIER_COLORS[link.tier], color: TIER_COLORS[link.tier] }}>
                                      {link.tier}
                                    </span>
                                  </td>
                                  <td>{link.deal_value ? `${link.currency || 'GBP'} ${parseFloat(link.deal_value).toLocaleString()}` : '—'}</td>
                                  <td>
                                    {link.start_date || link.end_date
                                      ? `${link.start_date ? new Date(link.start_date).toLocaleDateString('en-GB') : '?'} – ${link.end_date ? new Date(link.end_date).toLocaleDateString('en-GB') : 'ongoing'}`
                                      : '—'}
                                  </td>
                                  <td>{link.redemptions || 0}</td>
                                  <td>£{parseFloat(link.total_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                                  <td>
                                    <button
                                      className="btn-icon btn-danger-icon"
                                      title="Remove link"
                                      onClick={() => { if (window.confirm(`Remove ${link.club_name} from ${sponsor.business_name}?`)) unlinkMutation.mutate({ sponsorId: sponsor.id, clubId: link.club_id }); }}
                                    >
                                      <Unlink size={14} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      {sponsor.notes && (
                        <div className="panel-notes">
                          <strong>Notes:</strong> {sponsor.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Sponsor Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSponsor ? 'Edit Sponsor' : 'Add Sponsor'}</h2>
              <button className="btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleSubmit} className="sponsor-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Business Name *</label>
                  <input type="text" value={form.businessName} onChange={e => setForm(f => ({...f, businessName: e.target.value}))} placeholder="e.g. SportsPro Ltd" required />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                    <option value="">Select category...</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input type="text" value={form.contactName} onChange={e => setForm(f => ({...f, contactName: e.target.value}))} placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label>Contact Email</label>
                  <input type="email" value={form.contactEmail} onChange={e => setForm(f => ({...f, contactEmail: e.target.value}))} placeholder="email@business.com" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input type="text" value={form.contactPhone} onChange={e => setForm(f => ({...f, contactPhone: e.target.value}))} placeholder="+44..." />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input type="url" value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} placeholder="https://..." />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input type="url" value={form.logoUrl} onChange={e => setForm(f => ({...f, logoUrl: e.target.value}))} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label>Geographic Scope</label>
                  <select value={form.geographicScope} onChange={e => setForm(f => ({...f, geographicScope: e.target.value}))}>
                    {SCOPES.map(s => <option key={s} value={s}>{SCOPE_ICONS[s]} {s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Brief description of the sponsor..." rows={2} />
              </div>

              <div className="form-group">
                <label>Internal Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Deal context, relationship history, etc." rows={2} />
              </div>

              {formError && (
                <div className="form-error"><AlertCircle size={14} /> {formError}</div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={createMutation.isPending || editMutation.isPending}>
                  {(createMutation.isPending || editMutation.isPending) ? 'Saving...' : editingSponsor ? 'Save Changes' : 'Add Sponsor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Link to Club Modal */}
      {linkModal && (
        <div className="modal-overlay" onClick={() => setLinkModal(null)}>
          <div className="modal-box modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link to Club</h2>
              <button className="btn-icon" onClick={() => setLinkModal(null)}><X size={18} /></button>
            </div>
            <p className="modal-subtext">Linking <strong>{linkModal.sponsorName}</strong> to a club</p>

            <form onSubmit={handleLink}>
              <div className="form-group">
                <label>Club *</label>
                <select
                  value={linkForm.clubId}
                  onChange={e => setLinkForm(f => ({...f, clubId: e.target.value, sponsorId: ''}))}
                  required
                >
                  <option value="">Select a club...</option>
                  {availableClubs.map(c => (
                    <option key={c.id} value={c.id}>{c.name} {c.sport ? `(${c.sport})` : ''}</option>
                  ))}
                </select>
                {availableClubs.length === 0 && <small className="hint-text">All clubs are already linked.</small>}
              </div>

              {linkForm.clubId && (
                <div className="form-group">
                  <label>Link to existing club sponsor <span className="label-optional">(for attribution)</span></label>
                  <select value={linkForm.sponsorId} onChange={e => setLinkForm(f => ({...f, sponsorId: e.target.value}))}>
                    <option value="">— Not linked to a club sponsor yet —</option>
                    {clubLevelSponsors.map(s => (
                      <option key={s.id} value={s.id}>{s.business_name}{s.tier !== 'standard' ? ` (${s.tier})` : ''}</option>
                    ))}
                  </select>
                  {clubLevelSponsors.length === 0
                    ? <small className="hint-text">No sponsors set up in this club yet. You can link later once a club admin adds them.</small>
                    : <small className="hint-text">Selecting a match here joins the platform sponsor to real QR redemption data from that club.</small>
                  }
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Tier</label>
                  <select value={linkForm.tier} onChange={e => setLinkForm(f => ({...f, tier: e.target.value}))}>
                    {TIERS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Deal Value (£)</label>
                  <input type="number" min="0" step="0.01" value={linkForm.dealValue} onChange={e => setLinkForm(f => ({...f, dealValue: e.target.value}))} placeholder="0.00" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" value={linkForm.startDate} onChange={e => setLinkForm(f => ({...f, startDate: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={linkForm.endDate} onChange={e => setLinkForm(f => ({...f, endDate: e.target.value}))} />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={linkForm.notes} onChange={e => setLinkForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Deal specifics, renewal terms..." />
              </div>

              {linkError && <div className="form-error"><AlertCircle size={14} /> {linkError}</div>}

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setLinkModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={linkMutation.isPending}>
                  {linkMutation.isPending ? 'Linking...' : <><Check size={14} /> Link Club</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PlatformLayout>
  );
}
